import { SuperInput } from "@components:SuperInput/SuperInput";
import { IconButton, InputAdornment, SxProps, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdBackspace, MdVisibility, MdVisibilityOff } from "react-icons/md";

import { useSchemaForm } from "../context";
import { findMaskedSecrets, isMaskedSecret } from "../secrets";

/*
 * A write-only secret such as an API key.
 * A mask that keeps a stored secret (see secrets.ts) shows as an empty field with an "Unchanged" placeholder,
 * and clearing a typed value puts the mask back. Any other mask shows as an empty field.
 * The erase button sets the value to "" (not null).
 */
export const SecretInput = ({
  path,
  value,
  onChange,
  label,
  errorText,
  disabled,
  sx,
}: {
  path: string;
  value: unknown;
  onChange: (value: string) => void;
  label: string;
  errorText?: string;
  disabled?: boolean;
  sx?: SxProps;
}) => {
  const { t } = useTranslation();
  const { schema, rootPath, stored } = useSchemaForm();
  const { getValues } = useFormContext();
  const [visible, setVisible] = useState(false);
  const text = typeof value === "string" ? value : "";
  const masked = isMaskedSecret(text);
  const keepsStored =
    masked &&
    findMaskedSecrets(
      schema,
      rootPath ? getValues(rootPath) : getValues(),
      stored,
    ).some((secret) => secret.kept && secret.path === path);

  // The mask last seen, so an emptied field means "keep the stored secret"
  const mask = useRef<string | null>(null);
  useEffect(() => {
    if (keepsStored) mask.current = text;
  }, [keepsStored, text]);

  return (
    <SuperInput.TextField
      type={visible ? "text" : "password"}
      value={masked ? "" : text}
      placeholder={keepsStored ? t("schemaForm.unchanged") : undefined}
      onChange={(e) => {
        const next = e.target.value;
        onChange(next === "" && mask.current ? mask.current : next);
      }}
      disabled={disabled}
      errorText={errorText}
      sx={sx}
      slotProps={{
        htmlInput: { "aria-label": label },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {/* Keys: SuperInput deep-merges slotProps, which copies these elements */}
              <Tooltip key="clear" title={t("schemaForm.clearSecret")}>
                <span>
                  <IconButton
                    aria-label={t("schemaForm.clearSecret")}
                    onClick={() => {
                      mask.current = null;
                      onChange("");
                    }}
                    disabled={disabled || text === ""}
                    size="small"
                  >
                    <MdBackspace />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton
                key="visibility"
                aria-label={t(
                  visible ? "schemaForm.hideSecret" : "schemaForm.showSecret",
                )}
                onClick={() => setVisible((shown) => !shown)}
                disabled={disabled || masked}
                edge="end"
                size="small"
              >
                {visible ? <MdVisibilityOff /> : <MdVisibility />}
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
